import { supabase } from "@/integrations/supabase/client";

export type AccessAction = "invite" | "reset" | "status";

export interface AccessResult {
  success: boolean;
  code?: string;
  exists?: boolean;
  message?: string;
  error?: string;
}

async function call(
  action: AccessAction,
  email: string,
  fullName?: string,
): Promise<AccessResult> {
  const { data, error } = await supabase.functions.invoke("admin-user-access", {
    body: {
      action,
      email,
      full_name: fullName || undefined,
      origin: window.location.origin,
    },
  });

  if (error) {
    let message = error.message || "The request failed. Please try again.";
    try {
      const body = await (error as any)?.context?.json?.();
      if (body?.error) message = body.error;
      if (body?.message) message = body.message;
    } catch {
      // keep the fallback message
    }
    return { success: false, message };
  }

  return (data as AccessResult) || { success: false, message: "No response." };
}

export const sendInvite = (email: string, fullName?: string) =>
  call("invite", email, fullName);

export const sendPasswordReset = (email: string) => call("reset", email);

export const hasAuthAccount = async (email: string): Promise<boolean | null> => {
  const res = await call("status", email);
  return res.success ? !!res.exists : null;
};

export interface PortalInviteResult {
  success: boolean;
  created?: boolean;
  existed?: boolean;
  code?: string;
  message?: string;
}

/**
 * Hands an existing member the keys to the portal: an account if they have none,
 * and a link that lets them set their own password.
 */
export async function invitePortal(memberRecordId: string): Promise<PortalInviteResult> {
  const { data, error } = await supabase.functions.invoke("portal-invite", {
    body: { member_record_id: memberRecordId, origin: window.location.origin },
  });

  if (error) {
    let message = error.message || "The request failed. Please try again.";
    try {
      const body = await (error as any)?.context?.json?.();
      if (body?.error) message = body.error;
      if (body?.message) message = body.message;
    } catch {
      // keep the fallback message
    }
    return { success: false, message };
  }

  return (data as PortalInviteResult) || { success: false, message: "No response." };
}

export interface PendingInvite {
  token: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  created_at: string;
  expires_at: string;
  una_amount: string | null;
  monthly_amount: string | null;
  url: string;
}

export interface ClientInviteResult {
  success: boolean;
  url?: string;
  expires_at?: string;
  reused?: boolean;
  sent?: boolean;
  parked?: boolean;
  message?: string;
}

async function callClientInvite(body: Record<string, unknown>): Promise<any> {
  const { data, error } = await supabase.functions.invoke("client-invite", { body });
  if (error) {
    let message = error.message || "The request failed. Please try again.";
    try {
      const parsed = await (error as any)?.context?.json?.();
      if (parsed?.error) message = parsed.error;
      if (parsed?.message) message = parsed.message;
    } catch {
      // keep the fallback message
    }
    return { success: false, message };
  }
  return data || { success: false, message: "No response." };
}

/** Mints or reuses the welcome link for this person and emails it to them. */
export async function sendClientInvite(input: {
  email: string;
  first_name?: string;
  last_name?: string;
  una_amount?: string;
  monthly_amount?: string;
}): Promise<ClientInviteResult> {
  return (await callClientInvite({ action: "send", ...input })) as ClientInviteResult;
}

/** Every welcome link that is still open. */
export async function listPendingInvites(): Promise<PendingInvite[]> {
  const res = await callClientInvite({ action: "list" });
  return res?.success ? (res.invites as PendingInvite[]) : [];
}
